import { useMemo } from 'react';

import { LogicalOperator } from '@frontend/api';
import { Box, Divider } from '@frontend/components';

import { BetweenItemDropBorder } from 'components/BetweenItemDropBorder';
import {
  CUSTOM_DRAGGABLE_ITEM_TYPES,
  DROP_RESULT_TYPES,
} from 'components/customDragLayer';
import { useDrop } from 'components/customDragLayer/hooks/useDrop';
import type {
  DraggableItem,
  DropResult,
} from 'components/customDragLayer/types';

import { NamedDivider } from './NamedDivider';

type AcceptableItemType = typeof CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_SIGNAL;

type BetweenSignalDropBorderProps = {
  side: 'top' | 'bottom';
  signalIndex: number;
  onReorderSignals: (oldIndex: number, newIndex: number) => void;
  isFirstSignal: boolean;
  isLastSignal: boolean;
  logicalOperator: LogicalOperator;
};

export const BetweenSignalDropBorder = ({
  side,
  signalIndex,
  onReorderSignals,
  isFirstSignal,
  isLastSignal,
  logicalOperator,
}: BetweenSignalDropBorderProps) => {
  const handleDrop = (
    droppedItem: DraggableItem<AcceptableItemType>,
  ): DropResult | undefined => {
    if (
      droppedItem.draggableItemType === CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_SIGNAL
    ) {
      const oldIndex = droppedItem.signal.signalIndex;
      const newIndex = signalIndex;

      // Avoid no-op drops
      if (newIndex === oldIndex || newIndex === oldIndex + 1) {
        return {
          dropResultType: DROP_RESULT_TYPES.RULES_REORDER_SIGNALS,
          didReorder: false,
        };
      }

      onReorderSignals(oldIndex, newIndex);

      return {
        dropResultType: DROP_RESULT_TYPES.RULES_REORDER_SIGNALS,
        didReorder: true,
      };
    }

    return undefined;
  };

  const [{ canDrop, isOver }, connectDropTarget] = useDrop({
    accept: CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_SIGNAL,
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver({ shallow: true }),
    }),
    onDropEnd: handleDrop,
  });

  // Memoize common props for BetweenItemDropBorder components
  const commonDropBorderProps = useMemo(
    () => ({
      ref: connectDropTarget,
      side,
      canDrop,
      isOver,
      dropAreaProps: { height: 5 },
    }),
    [connectDropTarget, side, canDrop, isOver],
  );

  // Calculate opacity for dividers when drop area is active
  const dividerOpacity = isOver ? 0 : 1;

  // If divider should be shown, wrap it with the drop border
  return side === 'top' ? (
    <>
      {isFirstSignal ? (
        <BetweenItemDropBorder {...commonDropBorderProps} />
      ) : (
        <Box position="relative">
          <NamedDivider
            label={logicalOperator}
            opacity={dividerOpacity}
            transition="opacity 150ms ease"
          />
          <BetweenItemDropBorder
            {...commonDropBorderProps}
            centerInContainer={true}
          />
        </Box>
      )}
    </>
  ) : side === 'bottom' && isLastSignal ? (
    <Box position="relative">
      <Divider
        variant="dashed"
        opacity={dividerOpacity}
        transition="opacity 150ms ease"
      />
      <BetweenItemDropBorder {...commonDropBorderProps} />
    </Box>
  ) : null;
};
